import {
  ReactNode,
  useEffect,
  useState,
  createContext,
  useCallback,
  useMemo,
} from 'react';
import { useSelector } from 'react-redux';
import Loader from '@/components/loader/loader';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useAppDispatch, persistor } from '@/store';
import { authActions, authSelectors } from '@/store/auth';
import { caregiverThunkActions } from '@/store/caregiver';
import { motherThunkActions } from '@/store/mother';
import { contentConsentThunkActions } from '@/store/content/consent';
import { documentThunkActions } from '@/store/document';
import { settingActions, settingThunkActions } from '@/store/settings';
import { staticDataThunkActions } from '@/store/static-data';
import { userSelectors, userThunkActions } from '@/store/user';
import { infantThunkActions } from '@/store/infant';
import {
  healthCareWorkerSelectors,
  healthCareWorkerThunkActions,
} from '@/store/healthCareWorker';
import { communitySelectors, communityThunkActions } from '@/store/community';
import { calendarThunkActions } from '@/store/calendar';
import { subMonths } from 'date-fns';
import { getNextFetchStartDate } from './utils/points/nextFetchDate.utils';
import {
  clearOfflineCredentials,
  clearUserScopedStoreIfUserChanged,
} from '@/utils/user/user-scoped-store.utils';

type IntialStoreSetupContextValues = {
  initloading: boolean;
  resetAuth: () => Promise<void>;
  getLoadingMessage: () => string;
  initStoreSetup: () => Promise<void>;
};

export const IntialStoreSetupContext =
  createContext<IntialStoreSetupContextValues>(
    {} as IntialStoreSetupContextValues
  );

export default function InitialStoreSetup({
  children,
}: {
  children?: ReactNode;
}) {
  const dispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();

  const [initloading, setInitLoading] = useState(false);
  const [staticDataLoading, setStaticDataLoading] = useState(false);
  const [otherLoading, setOtherLoading] = useState(false);

  const authUser = useSelector(authSelectors.getAuthUser);
  const userData = useSelector(userSelectors.getUser);
  const userId = userData?.id;
  const healthCareWorker = useSelector(
    healthCareWorkerSelectors.getHealthCareWorker
  );
  const userClinic = useSelector(communitySelectors.getClinicSelector);
  const completedPointItems = useSelector(
    healthCareWorkerSelectors.getHealthCareWorkerCompletedPointsDetailsSelector
  );

  // -------------------------
  // HELPERS
  // -------------------------

  const resetAuth = useCallback(async () => {
    await persistor.flush();
    await dispatch(authActions.resetAuthState());
  }, [dispatch]);

  const run = (list: Promise<any>[]) => Promise.all(list);

  // -------------------------
  // USER SWITCH SAFETY NET
  // Offline-first keeps the previous CHW data bag after logout. If a different
  // user authenticates, wipe that bag before any loaders run against it.
  // Same-user re-login leaves the bag intact (no extra network pull).
  // -------------------------
  useEffect(() => {
    if (!authUser?.id) return;
    if (userData?.id && userData.id !== authUser.id) {
      const switched = clearUserScopedStoreIfUserChanged(authUser.id);
      if (switched) {
        clearOfflineCredentials();
      }
    }
  }, [authUser?.id, userData?.id]);

  // -------------------------
  // INITIAL USER-DEPENDENT LOAD
  // -------------------------
  // Keyed on the user id, not the user object. updateUser replaces state.user
  // with a new object, so depending on the object made every profile edit
  // (e.g. saving a profile photo) re-run this load, flip initloading and
  // unmount `children` — tearing down the dialog provider and the open page
  // mid-flow. The load only needs to happen once per signed-in user.
  useEffect(() => {
    if (!userId) return;

    setInitLoading(true);

    run([
      dispatch(
        healthCareWorkerThunkActions.getHealthCareWorkerByUserId({})
      ).unwrap(),
      dispatch(caregiverThunkActions.getAllCaregiverClients({})),
    ]).finally(() => setInitLoading(false));
  }, [dispatch, userId]);

  // -------------------------
  // LOAD HCW-DEPENDENT DATA
  // -------------------------
  useEffect(() => {
    if (!healthCareWorker) return;

    const load = async () => {
      const items: Promise<any>[] = [];

      if (healthCareWorker.clinicId) {
        items.push(
          dispatch(
            communityThunkActions.getClinicById({
              clinicId: healthCareWorker.clinicId,
            })
          ).unwrap()
        );
      }

      items.push(
        dispatch(
          caregiverThunkActions.getCaregiversForHealthCareWorker({})
        ).unwrap()
      );

      items.push(
        dispatch(
          healthCareWorkerThunkActions.getHealthCareWorkerPointsTodoItems({})
        ).unwrap()
      );

      if (healthCareWorker.user?.id) {
        const startDate = getNextFetchStartDate(completedPointItems);
        // End date: null = "up to now" (recommended when fetching latest)
        const endDate = undefined;

        // Fetch only what's missing or new
        items.push(
          dispatch(
            healthCareWorkerThunkActions.getHealthCareWorkerPoints({
              startDate,
              endDate,
            })
          ).unwrap()
        );
        items.push(
          dispatch(
            healthCareWorkerThunkActions.getHealthCareWorkerTeamStanding({
              userId: healthCareWorker.user.id,
            })
          ).unwrap()
        );
      }

      await run(items);
    };

    load();
  }, [dispatch, healthCareWorker]);

  useEffect(() => {
    if (!healthCareWorker || !isOnline) return;

    // Only fetch if we don't already have documents
    dispatch(documentThunkActions.getDocumentsForHCW()).unwrap();
  }, [dispatch, healthCareWorker, isOnline]); // Only depends on HCW and online status

  // -------------------------
  // LOAD LEAGUE DATA
  // -------------------------
  useEffect(() => {
    if (!userClinic?.league?.id) return;

    dispatch(
      communityThunkActions.getLeagueById({ leagueId: userClinic.league.id })
    ).unwrap();
  }, [dispatch, userClinic]);

  // -------------------------
  // STATIC STORE
  // -------------------------
  const initStaticStoreSetup = useCallback(async () => {
    if (!isOnline) return;

    setStaticDataLoading(true);

    const year = new Date().getFullYear();

    await run([
      dispatch(
        contentConsentThunkActions.getConsent({ locale: 'en-za' })
      ).unwrap(),
      dispatch(settingThunkActions.getSettings({})).unwrap(),
      dispatch(staticDataThunkActions.getRelations()).unwrap(),
      dispatch(staticDataThunkActions.getGenders({})).unwrap(),
      dispatch(staticDataThunkActions.getRaces({})).unwrap(),
      dispatch(staticDataThunkActions.getLanguages({})).unwrap(),
      dispatch(staticDataThunkActions.getEducationLevels({})).unwrap(),
      dispatch(staticDataThunkActions.getHolidays({ year })).unwrap(),
      dispatch(staticDataThunkActions.getProvinces({})).unwrap(),
      dispatch(staticDataThunkActions.getReasonsForLeaving({})).unwrap(),
      dispatch(staticDataThunkActions.getDocumentTypes({})).unwrap(),
      dispatch(staticDataThunkActions.getVisitTypes({})).unwrap(),
      dispatch(staticDataThunkActions.getWorkflowStatuses({})).unwrap(),
      dispatch(
        calendarThunkActions.getCalendarEventTypes({ locale: 'en-za' })
      ).unwrap(),
      dispatch(infantThunkActions.getAllInfantEventRecordTypes()).unwrap(),
      dispatch(motherThunkActions.getAllMotherEventRecordTypes()).unwrap(),
    ]);

    setStaticDataLoading(false);
  }, [dispatch, isOnline]);

  // -------------------------
  // ADDITIONAL STORE DATA
  // -------------------------
  const initAdditionalStoreSetup = useCallback(async () => {
    if (!isOnline) return;

    setOtherLoading(true);

    const items: Promise<any>[] = [];

    items.push(dispatch(motherThunkActions.getMothers()).unwrap());
    items.push(dispatch(infantThunkActions.getInfants()).unwrap());

    // -------------------------------------------------
    // Always-needed additional data
    // -------------------------------------------------
    items.push(dispatch(userThunkActions.getUser({})).unwrap());
    items.push(dispatch(userThunkActions.getUserConsents({})).unwrap());
    items.push(
      dispatch(
        calendarThunkActions.getCalendarEvents({
          start: subMonths(new Date(), 1),
        })
      )
    );

    await run(items);

    setOtherLoading(false);
  }, [dispatch, isOnline]);

  // -------------------------
  // MAIN SETUP FUNCTION
  // -------------------------
  const initStoreSetup = useCallback(async () => {
    if (!isOnline) return;

    setInitLoading(true);

    await initStaticStoreSetup();
    await initAdditionalStoreSetup();

    dispatch(settingActions.setLastDataSync());
    setInitLoading(false);
  }, [dispatch, isOnline, initStaticStoreSetup, initAdditionalStoreSetup]);

  // Run once
  useEffect(() => {
    initStoreSetup();
  }, []);

  const getLoadingMessage = useCallback(() => {
    if (staticDataLoading) return 'Loading static data…';
    if (otherLoading) return 'Loading other data…';
    return 'Loading…';
  }, [staticDataLoading, otherLoading]);

  const value = useMemo(
    () => ({
      initloading,
      resetAuth,
      initStoreSetup,
      getLoadingMessage,
    }),
    [initloading, resetAuth, initStoreSetup, getLoadingMessage]
  );

  return (
    <IntialStoreSetupContext.Provider value={value}>
      {initloading ? <Loader loadingMessage={getLoadingMessage()} /> : children}
    </IntialStoreSetupContext.Provider>
  );
}
